/**
 * The pins, drawn over the sheet (`KLQ-20260820-001` phase 4).
 *
 * **It knows nothing about the renderer.** Everything it needs arrives as a `SheetTransform`
 * (`overlay.ts`), which is the shared seam ADR-0006 asked for. Swapping OpenSeadragon for a
 * PDF.js path would replace the transform and leave this file untouched.
 *
 * **Panning does not re-render React.** A marker's position is written straight onto its node
 * inside the transform's subscription, because `animation` fires once per frame and re-running
 * a component tree at 60 Hz to move a dozen absolutely-positioned dots is work nobody asked
 * for. React owns *which* markers exist and *how* they look; the subscription owns only where
 * they are.
 *
 * **A marker on another page is not rendered.** `anchor_ref.page` came in with E3 for exactly
 * this reason — before it, a pin placed on page 2 was stored indistinguishably from one on
 * page 1 and would be drawn on page 1.
 *
 * **Placing goes through the transform, not through this layer's DOM.** The layer stays
 * `pointer-events: none` so that a pan is a pan; a press that meant a placement arrives on
 * `SheetTransform.subscribePress`, already in sheet coordinates and already filtered for drags.
 * This layer's only part in it is the pending marker, and the composer it holds in place — both
 * projected on the same tick as every other marker, because a pin and the words about it
 * drifting apart for a frame is the one artefact a person would actually notice.
 */

import { useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';

import { useMessages } from '@/lib/i18n';

import type { SheetAnchor, SheetTransform } from './overlay';
import styles from './Viewer.module.css';

/** The projection map's key for the pending marker. Never a pin id — there is no pin yet. */
const PENDING_ID = 'pending';

/** One pin, reduced to what a marker needs. Numbering and carry-forward are decided upstream. */
export interface PinMarker {
  readonly id: string;
  readonly anchor: SheetAnchor;
  /** ①②③ — the same number the matching comment card carries. */
  readonly number: number;
  readonly resolved: boolean;
}

export interface PinLayerProps {
  /** `null` until the canvas has opened. No transform, no markers — never a guessed position. */
  readonly transform: SheetTransform | null;
  readonly markers: readonly PinMarker[];
  readonly selectedPinId: string | null;
  readonly onSelect: (pinId: string) => void;
  /**
   * Where the person pressed, before anything has been written. Drawn as a marker with no
   * number, because a number is a promise that the rail has a card for it — and there is no
   * card until the one request lands.
   */
  readonly pending?: SheetAnchor | null | undefined;
  /** The composer, anchored beside `pending`. Rendered by the caller, positioned here. */
  readonly children?: ReactNode;
}

export function PinLayer({
  transform,
  markers,
  selectedPinId,
  onSelect,
  pending = null,
  children,
}: PinLayerProps) {
  const t = useMessages('drawings');
  const nodes = useRef(new Map<string, HTMLElement>());

  // The pending marker joins the projection as one more entry rather than as a second
  // subscription. Memoised because it is the effect's dependency: a fresh array per render
  // would tear the transform's subscription down and rebuild it on every keystroke in the
  // composer above it.
  const placed = useMemo<readonly { readonly id: string; readonly anchor: SheetAnchor }[]>(
    () => (pending === null ? markers : [...markers, { id: PENDING_ID, anchor: pending }]),
    [markers, pending],
  );

  // A layout effect, and the reason is the composer rather than the flicker: every marker is
  // rendered `visibility: hidden` and made visible here, and an element whose computed
  // `visibility` is `hidden` cannot take focus. A passive effect in this parent runs *after*
  // the child's, so `PinComposer`'s mount focus would land on a still-hidden box and silently
  // do nothing. Layout effects run before any passive one, so the pending anchor is projected
  // and visible by the time the composer inside it asks for the caret.
  useLayoutEffect(() => {
    if (transform === null) return undefined;
    const project = (): void => {
      for (const marker of placed) {
        const node = nodes.current.get(marker.id);
        if (node === undefined) continue;
        const at = marker.anchor.page === transform.page ? transform.toScreen(marker.anchor) : null;
        if (at === null) {
          // `visibility` rather than `hidden`: an inline style is the only thing that reliably
          // outranks the class's own `display`, and a marker off this page is coming back the
          // moment the page does.
          node.style.visibility = 'hidden';
          continue;
        }
        node.style.visibility = 'visible';
        node.style.left = `${String(at.left)}px`;
        node.style.top = `${String(at.top)}px`;
      }
    };
    project();
    return transform.subscribe(project);
  }, [transform, placed]);

  if (transform === null) return null;

  return (
    <div className={styles.pinLayer}>
      {markers.map((marker) => (
        <button
          key={marker.id}
          type="button"
          ref={(node) => {
            if (node === null) nodes.current.delete(marker.id);
            else nodes.current.set(marker.id, node);
          }}
          className={[
            styles.pin,
            marker.resolved ? styles.pinResolved : '',
            marker.id === selectedPinId ? styles.pinSelected : '',
          ]
            .filter(Boolean)
            .join(' ')}
          // Positioned by the subscription above, so it starts off-screen rather than at the
          // sheet's corner: one frame of every pin stacked at 0,0 is a visible flicker.
          style={{ visibility: 'hidden' }}
          aria-label={t('pin.label', { number: marker.number })}
          aria-pressed={marker.id === selectedPinId}
          onClick={() => {
            onSelect(marker.id);
          }}
        >
          {/* The teardrop is rotated 45°, so its number is rotated back (design wireframe 2). */}
          <span className={styles.pinNumber}>{marker.number}</span>
        </button>
      ))}
      {pending === null ? null : (
        <div
          ref={(node) => {
            if (node === null) nodes.current.delete(PENDING_ID);
            else nodes.current.set(PENDING_ID, node);
          }}
          className={styles.pinPendingAnchor}
          style={{ visibility: 'hidden' }}
        >
          {/* Not a button: there is nothing to press it for. It marks the place the composer
              beside it is about, and the composer carries every control. */}
          <span className={[styles.pin, styles.pinPending].join(' ')} aria-hidden />
          {children}
        </div>
      )}
    </div>
  );
}
